import React from 'react';

import {
    Box,
    FormControlLabel,
    Grid,
    Paper,
    Switch,
    Typography,
} from '@mui/material';
import { Button } from '@apigrowfinance/pbolt-button';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import BasicDetailsShared from '@pages/flexControls/shared/BasicDetailsForm';
import getEnumTranslation from '@utils/getEnumTranslation';

interface BasicDetailsProps {
    goToNextStep?: () => void;
    isPrevious?: boolean;
    goToPreviousStep?: () => void;
}

const BasicDetails = ({
    goToNextStep,
    isPrevious,
    goToPreviousStep,
}: BasicDetailsProps) => {
    const {
        control,
        formState: { isDirty },
    } = useFormContext();
    const { theme } = useTheme();

    return (
        <Paper
            elevation={0}
            sx={{
                mt: 2,
                p: 2,
                pt: 7.5,
            }}>
            <Grid container spacing={2} flexDirection="row">
                <BasicDetailsShared isEditing={false} />
                <Grid item xs={12}>
                    <Controller
                        name="active"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between">
                                <Typography variant="h6">
                                    <FormattedMessage
                                        id="BasicDetails.enabledDisabled"
                                        defaultMessage="Once added would you like the control to be
                                    enabled or disabled?"
                                    />
                                </Typography>
                                <FormControlLabel
                                    sx={{
                                        color:
                                            value === true
                                                ? theme?.palette?.primary?.main
                                                : theme?.palette?.text?.primary,
                                    }}
                                    control={
                                        <Switch
                                            onChange={onChange}
                                            checked={value}
                                            data-testid="flex-control-switch"
                                        />
                                    }
                                    label={
                                        <Typography variant="overline">
                                            {getEnumTranslation(
                                                value === true
                                                    ? 'enabled'
                                                    : 'disabled',
                                            )}
                                        </Typography>
                                    }
                                />
                            </Box>
                        )}
                    />
                </Grid>
            </Grid>
            <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                sx={{ mt: 8, mb: 2 }}>
                {isPrevious && (
                    <Button
                        onClick={goToPreviousStep}
                        name="positive"
                        role="button"
                        variant="outlined"
                        disableElevation
                        data-testid="reset-period-form-previous-button"
                        sx={{
                            px: 8,
                            textTransform: 'none',
                        }}>
                        {getEnumTranslation('previous')}
                    </Button>
                )}
                <Button
                    disabled={!isDirty}
                    onClick={goToNextStep}
                    name="positive"
                    role="button"
                    variant="contained"
                    disableElevation
                    data-testid="basic-details-form-next-button"
                    sx={{
                        px: 8,
                        textTransform: 'none',
                    }}>
                    {getEnumTranslation('next')}
                </Button>
            </Box>
        </Paper>
    );
};

export default BasicDetails;
